import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, CreditCard, ListChecks, Inbox, Loader2, AlertTriangle, XCircle } from 'lucide-react';
import { REJECTION_REASONS_BY_CODE } from '../../components/self-service/rejectionReasons';
import PublicHeader from '../../components/self-service/PublicHeader';
import { useAuth } from '../../components/self-service/auth/useAuth';
import SignInModal from '../../components/self-service/auth/SignInModal';
import { formatMoneySmart } from '../../components/self-service/shared/format';
import { formatShortRange, formatDateTime as formatSubmittedAt } from '../../components/self-service/shared/dateFormat';
import { useAllPublisherConfigs, usePublisherConfig } from '../../data/publisherConfigs';
import type { PublisherConfig, SelfServiceSubmissionRow } from '../../types/selfService';
import { calculateImpressions } from '../../types/selfService';
import CampaignStatusDashboard from '../../components/self-service/CampaignStatusDashboard';
import {
  campaignDelivery, campaignDates, AdPlayProgress, Stat,
  statusToStep, bucketFor, bucketBadge,
  BUCKETS,
  ACTIVE_BUCKET_IDS,
  INACTIVE_BUCKET_IDS,
  sortRowsForBucket,
  applyOptimisticBlanketExtension,
  type BucketId,
} from '../../components/self-service/campaignSharedHelpers';
import { apiClient } from '../../lib/apiClient';
import { RerunMenu, FixAndResubmitButton, CancelButton, EditButton, ArchiveButton, ViewReportButton } from '../../components/self-service/campaignActions';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import Banner from '../../components/self-service/shared/Banner';
import ScheduledStartBanner from '../../components/self-service/shared/ScheduledStartBanner';

export type SubmissionRow = SelfServiceSubmissionRow;

// `creative_rejected` is purely status-driven: the banner must persist until
// the advertiser actually fixes & resubmits (status moves off the rejection
// state). `payment_failed` still uses `acknowledged_at` because that flow has
// its own resolution loop via the card-update prompt.
const needsAttention = (r: SubmissionRow) =>
  r.status === 'creative_rejected' ||
  (r.status === 'payment_failed' && !r.acknowledged_at);

const needsCelebration = (r: SubmissionRow): boolean => {
  if (r.status !== 'approved') return false;
  if (r.completion_acknowledged_at) return false;
  if (!r.campaign?.endDate) return false;
  const end = new Date(r.campaign.endDate.slice(0, 10) + 'T23:59:59').getTime();
  return Date.now() > end;
};


const formatMarkets = (codes: string[], lookup: Map<string, string>) => {
  if (!codes.length) return '—';
  const names = codes.map((c) => lookup.get(c) ?? c);
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
};

type MainTabId = 'active' | 'inactive';

const MAIN_TABS: Array<{ id: MainTabId; label: string }> = [
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
];

function bucketIdsForMainTab(tab: MainTabId): BucketId[] {
  return tab === 'active' ? ACTIVE_BUCKET_IDS : INACTIVE_BUCKET_IDS;
}

function mainTabForBucket(bucket: BucketId): MainTabId {
  return ACTIVE_BUCKET_IDS.includes(bucket) ? 'active' : 'inactive';
}

const MyCampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { user, isAuthenticated, selectedAdvertiser, advertiserMemberships, refreshMemberships } = useAuth();
  const { configs: allConfigs } = useAllPublisherConfigs();
  // Station-branded header for the closed loop.
  const { config: stationConfig } = usePublisherConfig(slug);
  // O(1) slug lookup — celebration/active sections re-render frequently and
  // each row used to do a linear find() through every publisher.
  const configsBySlug = React.useMemo(
    () => Object.fromEntries(allConfigs.map((c) => [c.slug, c])) as Record<string, PublisherConfig>,
    [allConfigs],
  );
  const getPublisherConfig = React.useCallback(
    (slug: string) => configsBySlug[slug] ?? null,
    [configsBySlug],
  );
  // CTV-468: retention signal — how often advertisers return to track progress.
  React.useEffect(() => {
    analytics.track(EVENTS.MY_CAMPAIGNS_VIEWED, { station_slug: slug });
  }, [slug]);
  const [signInOpen, setSignInOpen] = React.useState(!isAuthenticated);
  const [rows, setRows] = React.useState<SubmissionRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [mainTab, setMainTab] = React.useState<MainTabId>('active');
  const [expandedSections, setExpandedSections] = React.useState<Set<BucketId>>(
    () => new Set(BUCKETS.map((b) => b.id)),
  );

  const toggleSection = React.useCallback((bucketId: BucketId) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(bucketId)) next.delete(bucketId);
      else next.add(bucketId);
      return next;
    });
  }, []);

  const openSection = React.useCallback((bucketId: BucketId) => {
    setMainTab(mainTabForBucket(bucketId));
    setExpandedSections((prev) => new Set([...prev, bucketId]));
  }, []);

  // Rows awaiting the "Delivered!" celebration hoist out of the normal
  // buckets so they can feature above the tabs; they only settle into
  // Completed after the user acknowledges them.
  const celebrationRows = React.useMemo(() => rows.filter(needsCelebration), [rows]);
  const bucketableRows = React.useMemo(() => rows.filter((r) => !needsCelebration(r)), [rows]);
  const monthlyRecoveryRows = React.useMemo(() => rows
    .filter((r) => !!r.monthlyPaymentRecovery)
    .sort((a, b) => Date.parse(a.monthlyPaymentRecovery!.nextAttemptAt) - Date.parse(b.monthlyPaymentRecovery!.nextAttemptAt)), [rows]);
  const firstMonthlyRecovery = monthlyRecoveryRows[0] ?? null;

  const counts = React.useMemo(() => {
    const c: Record<BucketId, number> = { active: 0, scheduled: 0, completed: 0, pending: 0, archived: 0 };
    bucketableRows.forEach((r) => { c[bucketFor(r)]++; });
    return c;
  }, [bucketableRows]);

  const rowsByBucket = React.useMemo(() => {
    const grouped: Record<BucketId, SubmissionRow[]> = {
      active: [], scheduled: [], completed: [], pending: [], archived: [],
    };
    bucketableRows.forEach((r) => { grouped[bucketFor(r)].push(r); });
    (Object.keys(grouped) as BucketId[]).forEach((id) => {
      grouped[id] = sortRowsForBucket(grouped[id], id);
    });
    return grouped;
  }, [bucketableRows]);

  const mainTabCounts = React.useMemo(() => ({
    active: ACTIVE_BUCKET_IDS.reduce((n, id) => n + counts[id], 0),
    inactive: INACTIVE_BUCKET_IDS.reduce((n, id) => n + counts[id], 0),
  }), [counts]);

  // Auto-switch to Inactive ONCE on initial load when Active has nothing but
  // Inactive does.
  const didAutoPickRef = React.useRef(false);
  React.useEffect(() => {
    if (didAutoPickRef.current) return;
    if (rows.length === 0) return;
    didAutoPickRef.current = true;
    if (mainTabCounts.active > 0) return;
    if (mainTabCounts.inactive > 0) setMainTab('inactive');
  }, [mainTabCounts, rows.length]);

  const archivedSectionExpanded = mainTab === 'inactive' && expandedSections.has('archived');

  const attentionCount = React.useMemo(() => rows.filter(needsAttention).length, [rows]);
  const paymentAttentionCount = React.useMemo(
    () => rows.filter((r) => r.status === 'payment_failed' && !r.acknowledged_at).length,
    [rows],
  );
  // Where the banner & pulsing tab dot should point. `bucketFor()` routes
  // `creative_rejected` to Pending and `payment_failed` to Archived, so the
  // banner must follow whichever state is actually present. Rejections take
  // precedence in the mixed case — that's the action the user can take.
  const attentionBucket: BucketId = React.useMemo(
    () => (rows.some((r) => r.status === 'creative_rejected') ? 'pending' : 'archived'),
    [rows],
  );

  React.useEffect(() => { setSignInOpen(!isAuthenticated); }, [isAuthenticated]);

  // Seat redesign: campaigns are scoped to the currently-selected advertiser,
  // not to the user's email. Every accepted member of the seat sees every
  // submission, regardless of who submitted it. Switching the dropdown in
  // HeaderAuth triggers a re-fetch via the dependency below.
  //
  // Closed-loop scoping: the URL pins the station, so the request asks the
  // API to filter to just that station. Backend honors `stationSlug`.
  // `silent` mode (used by the focus listener) skips the loading-state flip
  // and keeps the existing rows on screen until the fetch returns — otherwise
  // refocusing the tab visibly remounts the whole page.
  const fetchRows = React.useCallback(async (advertiserId: string, silent = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await apiClient.post<{ rows: SubmissionRow[] }>(
        '/submissions/list-by-advertiser',
        { advertiserId, stationSlug: slug },
      );
      setRows(data.rows ?? []);
      if (silent) setError(null);
    } catch (e) {
      if (!silent) {
        setError(e instanceof Error ? e.message : 'Failed to load campaigns');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [slug]);

  // Distinguish "memberships still loading" (advertiserMemberships === null)
  // from "user truly has no memberships" (advertiserMemberships === [])
  // so the post-wizard transition doesn't flash the empty state while the
  // membership context is still hydrating. The earlier code flipped
  // loading=false the moment selectedAdvertiser was null, which caused a
  // false "No campaigns yet" right after a buyer's first submission.
  //
  // Note: we depend on a derived BOOLEAN (`membershipsLoaded`) rather than
  // the array itself so a caller/test that returns a fresh array reference
  // each render doesn't kick this effect into an infinite re-fetch loop.
  const membershipsLoaded = advertiserMemberships !== null;
  React.useEffect(() => {
    if (selectedAdvertiser?.advertiserId) {
      fetchRows(selectedAdvertiser.advertiserId);
    } else if (membershipsLoaded) {
      // Memberships have loaded; user genuinely has no advertiser seat.
      setRows([]);
      setLoading(false);
    }
    // membershipsLoaded === false → leave loading=true; the next render
    // (once /memberships/mine resolves) will pick the right branch above.
  }, [selectedAdvertiser?.advertiserId, membershipsLoaded, fetchRows]);

  // Defensive: post-wizard transition can land here before the auth context
  // has reflected a freshly-created advertiser seat. Force a fresh fetch on
  // mount so we don't depend on the wizard's refresh having propagated yet.
  React.useEffect(() => {
    if (isAuthenticated && !selectedAdvertiser) {
      void refreshMemberships();
    }
    // Intentionally not depending on selectedAdvertiser — we want this to
    // fire once on mount when the selectedAdvertiser slot is still empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Re-fetch when returning to the tab so approval/rejection done elsewhere
  // shows up without a manual reload.
  React.useEffect(() => {
    const advertiserId = selectedAdvertiser?.advertiserId;
    if (!advertiserId) return;
    const bump = () => fetchRows(advertiserId, true);
    window.addEventListener('focus', bump);
    return () => window.removeEventListener('focus', bump);
  }, [selectedAdvertiser?.advertiserId, fetchRows]);

  // Rows currently animating out of the celebration hero. Kept around as
  // celebrationRows until the collapse animation finishes so the card can
  // fade/shrink smoothly instead of vanishing instantly.
  const [dismissingIds, setDismissingIds] = React.useState<Set<string>>(new Set());
  const DISMISS_ANIMATION_MS = 500;

  const acknowledgeCompletion = React.useCallback(
    (submissionId: string) => {
      // Kick off the exit animation first.
      setDismissingIds((prev) => {
        const next = new Set(prev);
        next.add(submissionId);
        return next;
      });
      // Fire the ack and the collapse animation in parallel. The optimistic
      // commit only happens on success — if the POST fails (auth, network,
      // etc.) we roll dismissingIds back, set an error message, and leave
      // the row in `celebrationRows` so the user sees that dismiss didn't
      // stick. The prior fire-and-forget pattern hid a real persistence bug
      // behind the animation; this surfaces the failure.
      const ack = apiClient.post('/submissions/ack-completion', { submissionId });
      const animationDone = new Promise<void>((resolve) =>
        window.setTimeout(resolve, DISMISS_ANIMATION_MS),
      );
      Promise.all([ack, animationDone])
        .then(() => {
          setRows((prev) =>
            prev.map((r) =>
              r.id === submissionId
                ? { ...r, completion_acknowledged_at: new Date().toISOString() }
                : r,
            ),
          );
          setDismissingIds((prev) => {
            const next = new Set(prev);
            next.delete(submissionId);
            return next;
          });
        })
        .catch((e: unknown) => {
          console.error('ack-completion failed', e);
          setDismissingIds((prev) => {
            const next = new Set(prev);
            next.delete(submissionId);
            return next;
          });
          setError(
            e instanceof Error
              ? `Couldn't dismiss: ${e.message}`
              : "Couldn't dismiss the completion banner. Please try again.",
          );
        });
    },
    [],
  );

  // Acknowledge any unseen attention rows when the user expands Archived. Runs
  // once per expand with at least one unseen row; refetches the list so the
  // dot and banner drop immediately without requiring a full reload. Now
  // scoped to the selected advertiser — the badge is shared team state.
  React.useEffect(() => {
    const advertiserId = selectedAdvertiser?.advertiserId;
    if (!archivedSectionExpanded || attentionCount === 0 || !advertiserId) return;
    (async () => {
      try {
        await apiClient.post('/submissions/ack-attention', { advertiserId });
        analytics.track(EVENTS.CAMPAIGN_ATTENTION_ACKED, {
          station_slug: slug,
          advertiser_id: advertiserId,
          count: attentionCount,
        });
        fetchRows(advertiserId);
      } catch {
        // Non-fatal — dot will clear on next page load.
      }
    })();
  }, [archivedSectionExpanded, attentionCount, selectedAdvertiser?.advertiserId, fetchRows, slug]);

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 max-w-md w-full text-center">
          <ListChecks className="w-10 h-10 text-slate-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Sign in to view your campaigns</h1>
          <p className="text-sm text-slate-600 mb-6">
            Enter your email and we'll send a 6-digit code to sign in.
          </p>
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setSignInOpen(true)}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
            >
              Sign in
            </button>
          </div>
        </div>
        <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{`
        @keyframes live-pulse {
          0%   { transform: scale(1);    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.55); }
          40%  { transform: scale(1.18); box-shadow: 0 0 0 8px rgba(16, 185, 129, 0); }
          100% { transform: scale(1);    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }
        .live-pulse {
          animation: live-pulse 3.5s ease-out infinite;
          will-change: transform;
        }
        @keyframes attention-pulse {
          0%, 100% { transform: scale(1);   box-shadow: 0 0 0 2px white, 0 0 0 0 rgba(225, 29, 72, 0.55); }
          50%      { transform: scale(1.15); box-shadow: 0 0 0 2px white, 0 0 0 6px rgba(225, 29, 72, 0); }
        }
        .attention-dot {
          animation: attention-pulse 2s ease-out infinite;
        }
        @keyframes progress-shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
      <PublicHeader publisher={stationConfig ?? undefined} title="My Campaigns" />

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6">
        {loading ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading your campaigns…
          </div>
        ) : error ? (
          <Banner tone="warn" label="Couldn't load campaigns">{error}</Banner>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-700 font-medium">No campaigns yet</p>
            <p className="text-sm text-slate-500 mt-1">Visit a publisher's portal to start one.</p>
          </div>
        ) : (
          <>
            {/* Celebration hero — delivered campaigns awaiting acknowledgement.
                Shows above everything else so the "Delivered!" moment is the
                first thing the user sees when they land. */}
            {celebrationRows.length > 0 && (
              <div
                className="space-y-3 transition-all duration-[500ms] ease-in-out"
                style={{
                  // Collapses the section's bottom margin when every card is
                  // being dismissed so the page below slides up cleanly.
                  marginBottom: celebrationRows.every((r) => dismissingIds.has(r.id)) ? 0 : '1.25rem',
                }}
              >
                {celebrationRows.map((s) => {
                  const isDismissing = dismissingIds.has(s.id);
                  const cfg = getPublisherConfig(s.publisher_slug);
                  const publisherName = cfg?.name ?? s.publisher_slug;
                  const cardLabel = s.campaign?.campaignName?.trim() || s.business_info?.businessName || '—';
                  const progress = campaignDelivery(s);
                  const { startDate, endDate } = campaignDates(s);
                  return (
                    <div
                      key={s.id}
                      className="transition-all duration-[500ms] ease-in-out"
                      style={{
                        maxHeight: isDismissing ? '0px' : '800px',
                        opacity: isDismissing ? 0 : 1,
                        transform: isDismissing ? 'translateY(-16px) scale(0.96)' : 'translateY(0) scale(1)',
                        // When collapsed, hide overflow so the card visually
                        // shrinks; the fireworks/stamp are long gone by the
                        // time the user hits Done so clipping is safe.
                        overflow: isDismissing ? 'hidden' : 'visible',
                        marginTop: isDismissing ? '0' : undefined,
                      }}
                    >
                      <div
                      className="relative rounded-2xl border border-emerald-200 p-5"
                      style={{
                        background:
                          'linear-gradient(135deg, #ecfdf5 0%, #ffffff 55%, #d1fae5 100%)',
                        boxShadow:
                          '0 1px 0 rgba(255,255,255,0.8) inset, 0 0 0 1px rgba(16, 185, 129, 0.25), 0 10px 30px -10px rgba(16, 185, 129, 0.45)',
                      }}
                    >
                      {/* Brand-tinted glow in the top-right corner */}
                      <span
                        aria-hidden
                        className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl"
                        style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.3), transparent 70%)' }}
                      />

                      <div className="relative flex flex-wrap items-start justify-between gap-3 mb-3">
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700 mb-1">
                            Campaign complete · {publisherName}
                          </div>
                          <div className="text-xl font-semibold tracking-tight text-slate-900">
                            {cardLabel}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => acknowledgeCompletion(s.id)}
                          className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-semibold tracking-tight text-white transition-all hover:brightness-110 active:scale-[0.98]"
                          style={{
                            background: 'linear-gradient(180deg, #10b981 0%, #047857 100%)',
                            boxShadow:
                              '0 1px 0 rgba(255,255,255,0.35) inset, 0 6px 16px -6px rgba(16, 185, 129, 0.6)',
                          }}
                        >
                          Done
                        </button>
                      </div>

                      {progress.hasData && (
                        <AdPlayProgress
                          current={progress.current}
                          total={progress.total!}
                          percent={progress.percent}
                          startDate={startDate}
                          endDate={endDate}
                        />
                      )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {firstMonthlyRecovery && (
              <button
                type="button"
                onClick={() => navigate(`/${slug}/my-campaigns/${firstMonthlyRecovery.id}/update-payment`)}
                className="block w-full mb-4 text-left hover:brightness-[0.98] transition-all"
              >
                <Banner
                  tone="warn"
                  label={monthlyRecoveryRows.length === 1
                    ? 'A monthly campaign payment was declined'
                    : `${monthlyRecoveryRows.length} monthly campaign payments were declined`}
                  icon={<CreditCard className="w-3 h-3" />}
                  rightSlot={(
                    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-rose-600 px-2.5 py-1">
                      Update card →
                    </span>
                  )}
                >
                  Update the payment method by {formatSubmittedAt(firstMonthlyRecovery.monthlyPaymentRecovery!.nextAttemptAt)} to pay the missed month and keep future monthly charges running.
                </Banner>
              </button>
            )}

            {/* Attention banner — surfaces rejected/failed campaigns. Clicking
                it jumps to whichever tab actually holds the row (Pending for
                creative_rejected, Archived for payment_failed). Rejection
                banners persist until the advertiser fixes & resubmits. */}
            {attentionCount > 0 && (
              <button
                type="button"
                onClick={() => openSection(attentionBucket)}
                className="block w-full mb-4 text-left hover:brightness-[0.98] transition-all"
              >
                <Banner
                  tone="warn"
                  label={attentionCount === 1
                    ? '1 campaign needs your attention'
                    : `${attentionCount} campaigns need your attention`}
                  icon={<AlertTriangle className="w-3 h-3" />}
                  rightSlot={
                    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-rose-600 px-2.5 py-1">
                      View →
                    </span>
                  }
                >
                  {paymentAttentionCount > 0
                    ? 'A campaign payment needs an updated card before it can continue.'
                    : attentionCount === 1 ? 'A campaign was rejected.' : 'Some campaigns were rejected.'}
                </Banner>
              </button>
            )}

            {/* Active / Inactive tabs */}
            <div
              role="tablist"
              aria-label="Campaign status"
              className="inline-flex w-full p-1 rounded-full bg-slate-100 border border-slate-200/80 mb-4 gap-0.5"
            >
              {MAIN_TABS.map((t) => {
                const isSelected = t.id === mainTab;
                const count = mainTabCounts[t.id];
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    onClick={() => setMainTab(t.id)}
                    className={`relative flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[13px] font-semibold tracking-tight transition-all ${
                      isSelected
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {t.label}
                    <span
                      className={`text-[11px] font-semibold tabular-nums px-1.5 rounded-full ${
                        isSelected ? 'bg-slate-100 text-slate-700' : 'bg-slate-200/60 text-slate-500'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {bucketIdsForMainTab(mainTab).filter((id) => counts[id] > 0).length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
                <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">
                  Nothing in {MAIN_TABS.find((t) => t.id === mainTab)?.label.toLowerCase()}.
                </p>
              </div>
            ) : (
            <div className="space-y-2">
              {bucketIdsForMainTab(mainTab)
                .filter((bucketId) => counts[bucketId] > 0)
                .map((bucketId) => {
                const b = BUCKETS.find((x) => x.id === bucketId)!;
                const count = counts[bucketId];
                const expanded = expandedSections.has(bucketId);
                const showAttention = bucketId === attentionBucket && attentionCount > 0;
                const sectionRows = rowsByBucket[bucketId];

                return (
                  <div key={bucketId} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      type="button"
                      aria-expanded={expanded}
                      onClick={() => toggleSection(bucketId)}
                      className="relative w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="inline-flex items-center gap-2.5 min-w-0">
                        <ChevronDown
                          className={`w-4 h-4 shrink-0 text-slate-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                          aria-hidden
                        />
                        <span
                          className="w-1.5 h-1.5 rounded-full shrink-0"
                          style={{ backgroundColor: b.dot }}
                        />
                        <span className="text-[14px] font-semibold tracking-tight" style={{ color: b.text }}>
                          {b.label}
                        </span>
                      </span>
                      <span
                        className="text-[11px] font-semibold tabular-nums px-2 py-0.5 rounded-full shrink-0"
                        style={{
                          backgroundColor: b.countBgIdle,
                          color: b.countFg,
                        }}
                      >
                        {count}
                      </span>
                      {showAttention && (
                        <span
                          aria-label={`${attentionCount} unread`}
                          className="absolute top-3.5 right-14 attention-dot"
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '999px',
                            background: '#e11d48',
                            boxShadow: '0 0 0 2px white, 0 0 8px rgba(225, 29, 72, 0.65)',
                          }}
                        />
                      )}
                    </button>
                    {expanded && (
                      <div className="px-4 pb-4 border-t border-slate-100">
          <ul className="space-y-3 pt-3">
            {sectionRows.map((s) => {
              const cfg = getPublisherConfig(s.publisher_slug);
              const publisherName = cfg?.name ?? s.publisher_slug;
              const marketLookup = new Map((cfg?.markets ?? []).map((m) => [m.dmaCode, m.dmaName]));
              const cpmValue = s.line_item_cpm != null ? `$${s.line_item_cpm.toFixed(2)}` : '—';
              const cardLabel = s.campaign?.campaignName?.trim() || s.business_info?.businessName || '—';
              const badge = bucketBadge(s);
              const isRejected = s.status === 'creative_rejected';
              const isCanceled = s.status === 'canceled';
              const isPaymentFailed = s.status === 'payment_failed';

              return (
                <li key={s.id}>
                  <div
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(`/${slug}/my-campaigns/${s.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/${slug}/my-campaigns/${s.id}`);
                      }
                    }}
                    className="relative block bg-white border border-slate-200 rounded-xl p-5 hover:border-slate-300 hover:shadow-sm transition cursor-pointer"
                  >
                    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 mb-4 pt-1">
                      <span aria-hidden />
                      <div className="text-lg md:text-xl font-semibold tracking-tight text-slate-900 text-center">
                        {cardLabel}
                      </div>
                      <div className="flex items-center justify-end gap-2">
                        {cfg?.logoUrl && (
                          <img
                            src={cfg.logoUrl}
                            alt={publisherName}
                            className={`h-5 w-auto max-w-[72px] object-contain ${cfg.logoInvert === true ? 'brightness-0 invert' : ''}`}
                          />
                        )}
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded shrink-0 ${badge.className} ${bucketFor(s) === 'active' ? 'live-pulse' : ''}`}
                        >
                          {badge.label}
                        </span>
                      </div>
                    </div>
                    {(() => {
                      const isSingleMarket = (cfg?.markets?.length ?? 0) === 1;
                      const targetingMode = (s.campaign?.targetingMode ?? 'default') as 'default' | 'custom';
                      const targetingValue = isSingleMarket
                        ? (targetingMode === 'default'
                            ? ((cfg?.markets?.[0]?.channels ?? []).join(', ') || cfg?.name || '—')
                            : 'Custom geotargeting')
                        : formatMarkets(s.campaign?.targetMarkets ?? [], marketLookup);
                      const targetingLabel = isSingleMarket
                        ? (targetingMode === 'default' ? 'Channel' : 'Targeting')
                        : 'Markets';
                      return (
                        <div className="grid grid-cols-2 md:grid-cols-4 -mx-4">
                          <Stat label="Budget" value={s.campaign?.budget != null ? `$${formatMoneySmart(s.campaign.budget)}` : '—'} accentColor={cfg?.accentColor} />
                          <Stat label="Dates" value={formatShortRange(s.campaign?.startDate, s.campaign?.endDate)} accentColor={cfg?.accentColor} showRail />
                          <Stat label="CPM" value={cpmValue} accentColor={cfg?.accentColor} showRail />
                          <Stat label={targetingLabel} value={targetingValue} accentColor={cfg?.accentColor} showRail />
                        </div>
                      );
                    })()}
                    {isPaymentFailed && (
                      <div className="mt-5">
                        <Banner tone="warn" label="Payment needs attention">
                          Open this campaign to update your card and retry the charge.
                        </Banner>
                      </div>
                    )}
                    {s.monthlyPaymentRecovery && (
                      <div className="mt-5">
                        <Banner
                          tone="warn"
                          label="Monthly payment declined"
                          rightSlot={(
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/${slug}/my-campaigns/${s.id}/update-payment`);
                              }}
                              className="text-[11px] font-semibold uppercase tracking-[0.08em] text-rose-600 px-2 py-1"
                            >
                              Update card →
                            </button>
                          )}
                        >
                          Pay the missed monthly charge before the retry window closes at {formatSubmittedAt(s.monthlyPaymentRecovery.nextAttemptAt)}.
                        </Banner>
                      </div>
                    )}
                    {(() => {
                      if (!cfg || isRejected || isCanceled) return null;
                      const bucket = bucketFor(s);
                      const isActiveOrCompleted = s.status === 'approved' && (bucket === 'active' || bucket === 'completed');
                      if (isActiveOrCompleted) {
                        const progress = campaignDelivery(s);
                        const { startDate, endDate } = campaignDates(s);
                        return (
                          <>
                            {/* Progress bar — primary focus when active */}
                            {progress.hasData && (
                              <div className="mt-5 pt-4 border-t border-slate-100">
                                <AdPlayProgress
                                  current={progress.current}
                                  total={progress.total!}
                                  percent={progress.percent}
                                  startDate={startDate}
                                  endDate={endDate}
                                  prominent
                                />
                              </div>
                            )}
                          </>
                        );
                      }
                      // Scheduled (approved, not yet running) shows the full
                      // tracker with Delivering as the current pulsing step
                      // since the campaign is waiting to start.
                      const trackerStep = bucket === 'scheduled' ? 3 : statusToStep(s.status);
                      const isScheduled = bucket === 'scheduled';
                      const scheduledStart = isScheduled ? s.campaign?.startDate : null;
                      // CTV-516: scheduled / pending campaigns haven't started
                      // delivering yet, but the buyer has already booked a fixed
                      // number of impressions. Mirror the detail view: show the
                      // delivery bar empty (0 of the booked goal) so the goal is
                      // visible on the card before the campaign goes live. Booked
                      // impressions come from the line item's CPM when known,
                      // otherwise the selected product's CPM.
                      const cpm = s.line_item_cpm
                        ?? cfg.products.find((p) => p.id === s.campaign?.selectedProduct)?.cpm
                        ?? null;
                      const budget = s.campaign?.budget ?? null;
                      const booked = budget != null && cpm != null && cpm > 0
                        ? calculateImpressions(budget, cpm)
                        : null;
                      const showBookedBar = (bucket === 'scheduled' || bucket === 'pending') && booked != null && booked > 0;
                      const { startDate: bookedStart, endDate: bookedEnd } = campaignDates(s);
                      return (
                        <div className="mt-5 pt-4 border-t border-slate-100">
                          {showBookedBar && (
                            <div className="mb-4">
                              <AdPlayProgress
                                current={0}
                                total={booked!}
                                percent={0}
                                startDate={bookedStart}
                                endDate={bookedEnd}
                                prominent
                                hideDaysRemaining
                              />
                            </div>
                          )}
                          {isScheduled && scheduledStart && (
                            <ScheduledStartBanner startDate={scheduledStart} className="mb-4" />
                          )}
                          <CampaignStatusDashboard config={cfg} currentStep={trackerStep} compact />
                        </div>
                      );
                    })()}
                    {isRejected && (() => {
                      // CTV-466: per-creative rejection digest. If any creatives
                      // are voided with reviewer notes, render one block per
                      // file; otherwise fall back to the submission-level
                      // reason (CTV-457 path: whole-submission reject).
                      const voidedCreatives = (s.creatives ?? []).filter(
                        (c) =>
                          c.status === 'voided' &&
                          // CTV-611: a resolved rejection (resolved_at set)
                          // is already handled; the digest lists only
                          // unresolved ones.
                          !c.resolved_at &&
                          (c.rejection_reason || (c.rejection_reason_codes && c.rejection_reason_codes.length > 0)),
                      );
                      return (
                        <div className="mt-4 pt-4 border-t border-slate-100">
                          <Banner
                            tone="warn"
                            label={voidedCreatives.length > 1
                              ? `${voidedCreatives.length} commercials need updates from ${publisherName}`
                              : `Not approved by ${publisherName}`}
                            icon={<XCircle className="w-3 h-3" />}
                          >
                            {voidedCreatives.length > 0 ? (
                              <ul className="space-y-3">
                                {voidedCreatives.map((c) => (
                                  <li key={c.id}>
                                    <div className="font-medium text-rose-900 mb-0.5">{c.filename}</div>
                                    {c.rejection_reason_codes && c.rejection_reason_codes.length > 0 && (
                                      <ul className="space-y-0.5">
                                        {c.rejection_reason_codes.map((code) => {
                                          const r = REJECTION_REASONS_BY_CODE[code];
                                          if (!r) return null;
                                          return (
                                            <li key={code} className="text-rose-800 leading-relaxed">
                                              <span className="font-medium text-rose-900">{r.label}</span>
                                              {r.advertiserText && <> — <span className="text-rose-700/90">{r.advertiserText}</span></>}
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    )}
                                    {c.rejection_reason && (
                                      <div className="mt-1 pl-3 border-l-2 border-rose-200 text-rose-800 leading-relaxed italic">
                                        "{c.rejection_reason}"
                                      </div>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : (s.rejection_reason_codes && s.rejection_reason_codes.length > 0) ? (
                              <ul className="space-y-1">
                                {s.rejection_reason_codes.map((code) => {
                                  const r = REJECTION_REASONS_BY_CODE[code];
                                  if (!r) return null;
                                  return (
                                    <li key={code} className="leading-relaxed">
                                      <span className="font-medium text-rose-900">{r.label}</span>
                                      {r.advertiserText && <> — <span className="text-rose-700/90">{r.advertiserText}</span></>}
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              <p>Check your email for details.</p>
                            )}
                            {voidedCreatives.length === 0 && s.rejection_reason && (
                              <div className="mt-2 pl-3 border-l-2 border-rose-200 leading-relaxed italic">
                                "{s.rejection_reason}"
                              </div>
                            )}
                          </Banner>
                        </div>
                      );
                    })()}
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2 text-xs text-slate-500">
                      <span>
                        Submitted {formatSubmittedAt(s.created_at)}
                        {s.canceled_at && (
                          <span className="ml-2 pl-2 border-l border-slate-200">
                            Canceled {formatSubmittedAt(s.canceled_at)}
                          </span>
                        )}
                      </span>
                      <div className="flex items-center gap-2">
                        {/* CTV-445: straight-to-report shortcut for campaigns
                            that have delivery data (live or delivered). */}
                        {cfg && (bucketFor(s) === 'active' || bucketFor(s) === 'completed') && (
                          <ViewReportButton submission={s} cfg={cfg} />
                        )}
                        {/* CTV-466 follow-up: rejected campaigns now live in
                            the pending bucket; surface Fix & resubmit + Archive
                            directly on the card. */}
                        {cfg && (bucketFor(s) === 'archived' || isRejected) && s.creatives?.[0] && (
                          <FixAndResubmitButton submission={s} cfg={cfg} />
                        )}
                        {cfg && isRejected && (
                          <ArchiveButton
                            submission={s}
                            cfg={cfg}
                            onArchived={(id) =>
                              setRows((prev) =>
                                prev.map((r) =>
                                  r.id === id ? { ...r, status: 'archived' } : r,
                                ),
                              )
                            }
                          />
                        )}
                        {cfg && (bucketFor(s) === 'active' || bucketFor(s) === 'scheduled') && (
                          <EditButton
                            submission={s}
                            cfg={cfg}
                            onExtended={(id, newEndDate, addBudget) => {
                              setRows((prev) =>
                                prev.map((r) => {
                                  if (r.id !== id) return r;
                                  const previousEndDate = r.campaign?.endDate;
                                  const withBumpedCreatives = previousEndDate
                                    ? applyOptimisticBlanketExtension(r, previousEndDate, newEndDate)
                                    : r;
                                  return {
                                    ...withBumpedCreatives,
                                    campaign: {
                                      ...withBumpedCreatives.campaign,
                                      endDate: newEndDate,
                                      ...(addBudget > 0 && withBumpedCreatives.campaign?.budget != null
                                        ? { budget: withBumpedCreatives.campaign.budget + addBudget }
                                        : {}),
                                    },
                                  };
                                }),
                              );
                            }}
                            onCanceled={(id, canceledAt) => {
                              setRows((prev) =>
                                prev.map((r) =>
                                  r.id === id
                                    ? { ...r, status: 'canceled', canceled_at: canceledAt }
                                    : r,
                                ),
                              );
                            }}
                            onCreativeSwapped={() => {
                              // Reload the affected submission so the creative rows are fresh.
                              if (selectedAdvertiser?.advertiserId) {
                                void fetchRows(selectedAdvertiser.advertiserId, true);
                              }
                            }}
                          />
                        )}
                        {cfg && s.creatives?.[0] && bucketFor(s) !== 'archived' && !isRejected && (
                          <RerunMenu submission={s} cfg={cfg} />
                        )}
                        {cfg && bucketFor(s) !== 'active' && bucketFor(s) !== 'scheduled' && (
                          <CancelButton
                            submission={s}
                            cfg={cfg}
                            onCanceled={(id, canceledAt) => {
                              setRows((prev) =>
                                prev.map((r) =>
                                  r.id === id
                                    ? { ...r, status: 'canceled', canceled_at: canceledAt }
                                    : r,
                                ),
                              );
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default MyCampaignsPage;
